/**
 * Playable species for a 2024-rules table.
 *
 * SRD 5.2 species are CC-BY-4.0. Fairy and Harengon come from The Wild Beyond
 * the Witchlight, which is not open content: only their mechanics appear here
 * (game rules are not copyrightable) and every description is written fresh.
 * Their original 2014 write-ups are in the adventure book.
 */
import type { Ability } from '../constants'

export type SpeciesSource = 'srd' | 'wbtw'
export type Size = 'Small' | 'Medium' | 'Small or Medium'

export interface SpeciesTrait {
  name: string
  /** Plain-English, table-ready. Shown on the sheet and in the wizard. */
  text: string
}

export interface SpeciesChoice {
  /** Stored on the character as `speciesChoice`. */
  id: string
  label: string
  help: string
  options: { value: string; label: string; help: string; traits?: SpeciesTrait[] }[]
}

export interface SpeciesDef {
  id: string
  name: string
  source: SpeciesSource
  /** One line a brand-new player can decide from. */
  tagline: string
  /** Two or three sentences of what it feels like to play. */
  blurb: string
  size: Size
  speed: number
  /** Extra movement modes, e.g. { fly: 30 }. */
  otherSpeeds?: Record<string, number>
  darkvision: number
  creatureType: 'Humanoid' | 'Fey'
  traits: SpeciesTrait[]
  /** Sub-choices like Elven Lineage or Draconic Ancestry. */
  choice?: SpeciesChoice
  /** Fixed skill proficiencies this species grants. */
  skillProficiencies?: string[]
  /** Species that let you pick a skill / feat, handled in the wizard. */
  grantsSkillChoice?: number
  /** Restricts that pick to a specific list (Elf's Keen Senses). Omit for any skill. */
  skillChoiceFrom?: string[]
  grantsOriginFeat?: boolean
  /** Extra hit points per level (Dwarven Toughness). */
  hpPerLevel?: number
  /** Abilities whose saving throws get advantage. */
  saveAdvantage?: Ability[]
  witchlightNote?: string
}

export const SPECIES_LIST: SpeciesDef[] = [
  {
    id: 'human',
    name: 'Human',
    source: 'srd',
    tagline: 'Adaptable and quick to learn — the safest first pick.',
    blurb:
      'Humans get a free feat, an extra skill, and start most days with Heroic Inspiration to reroll a bad die. Nothing flashy, useful everywhere.',
    size: 'Small or Medium',
    speed: 30,
    darkvision: 0,
    creatureType: 'Humanoid',
    grantsSkillChoice: 1,
    grantsOriginFeat: true,
    traits: [
      { name: 'Resourceful', text: 'You gain Heroic Inspiration whenever you finish a Long Rest.' },
      { name: 'Skillful', text: 'You gain proficiency in one skill of your choice.' },
      { name: 'Versatile', text: 'You gain an Origin feat of your choice.' },
    ],
  },
  {
    id: 'fairy',
    name: 'Fairy',
    source: 'wbtw',
    tagline: 'Tiny, winged, and already a little bit magic. Very Feywild.',
    blurb:
      'You can fly from level 1, which changes how you approach almost every problem. You also get a few innate spells. Flight is off-limits in medium or heavy armour.',
    size: 'Small',
    speed: 30,
    otherSpeeds: { fly: 30 },
    darkvision: 0,
    creatureType: 'Fey',
    witchlightNote: 'Introduced in The Wild Beyond the Witchlight — a natural fit for this campaign.',
    traits: [
      {
        name: 'Flight',
        text: 'You have a flying speed equal to your walking speed. You cannot use it while wearing medium or heavy armour.',
      },
      {
        name: 'Fairy Magic',
        text: 'You know the Druidcraft cantrip. At 3rd level you can cast Faerie Fire once per long rest, and at 5th level Enlarge/Reduce once per long rest. Your spellcasting ability for these is Intelligence, Wisdom, or Charisma (chosen when you make the character).',
      },
      { name: 'Fey', text: 'Your creature type is Fey rather than Humanoid.' },
    ],
    choice: {
      id: 'fairyMagicAbility',
      label: 'Which ability powers your fairy magic?',
      help: 'Pick the one your class already uses, so you only track one number.',
      options: [
        { value: 'int', label: 'Intelligence', help: 'Match a Wizard.', traits: [{ name: 'Fairy Magic Ability', text: 'Your fairy magic uses Intelligence.' }] },
        { value: 'wis', label: 'Wisdom', help: 'Match a Cleric, Druid, or Ranger.', traits: [{ name: 'Fairy Magic Ability', text: 'Your fairy magic uses Wisdom.' }] },
        { value: 'cha', label: 'Charisma', help: 'Match a Bard, Paladin, Sorcerer, or Warlock.', traits: [{ name: 'Fairy Magic Ability', text: 'Your fairy magic uses Charisma.' }] },
      ],
    },
  },
  {
    id: 'harengon',
    name: 'Harengon',
    source: 'wbtw',
    tagline: 'Rabbitfolk. Fast, alert, and very hard to pin down.',
    blurb:
      'You act early in almost every fight, jump absurd distances, and shrug off effects that would knock other people over. Great if you like being the one who gets there first.',
    size: 'Small or Medium',
    speed: 30,
    darkvision: 0,
    creatureType: 'Humanoid',
    witchlightNote: 'Introduced in The Wild Beyond the Witchlight — a natural fit for this campaign.',
    skillProficiencies: ['perception'],
    traits: [
      { name: 'Hare-Trigger', text: 'You can add your Proficiency Bonus to your Initiative rolls.' },
      { name: 'Leporine Senses', text: 'You have proficiency in the Perception skill.' },
      {
        name: 'Lucky Footwork',
        text: 'When you fail a Dexterity saving throw, you can use your reaction to roll a d4 and add it to the result, possibly turning the failure into a success.',
      },
      {
        name: 'Rabbit Hop',
        text: 'As a Bonus Action you can jump a number of feet equal to five times your Proficiency Bonus without needing a running start. Usable Proficiency Bonus times per long rest.',
      },
    ],
  },
  {
    id: 'elf',
    name: 'Elf',
    source: 'srd',
    tagline: 'Keen-eyed and fey-blooded, with magic in the bloodline.',
    blurb:
      'Sharp senses, resistance to being charmed, and a free cantrip that grows into bigger spells. Pick a lineage to decide which flavour of elf you are.',
    size: 'Medium',
    speed: 30,
    darkvision: 60,
    creatureType: 'Humanoid',
    grantsSkillChoice: 1,
    skillChoiceFrom: ['insight', 'perception', 'survival'],
    witchlightNote: 'Elves come from the Feywild originally — very much at home in this story.',
    traits: [
      { name: 'Fey Ancestry', text: 'You have advantage on saving throws to avoid or end the Charmed condition.' },
      { name: 'Keen Senses', text: 'You have proficiency in one of Insight, Perception, or Survival.' },
      { name: 'Trance', text: 'You do not sleep. You finish a Long Rest in 4 hours of light meditation.' },
    ],
    choice: {
      id: 'elvenLineage',
      label: 'Elven lineage',
      help: 'This decides your free spells and one extra perk.',
      options: [
        {
          value: 'drow',
          label: 'Drow',
          help: 'Darkvision out to 120 feet, plus Dancing Lights.',
          traits: [{ name: 'Drow Lineage', text: 'Darkvision 120 ft. You know Dancing Lights. At 3rd level you can cast Faerie Fire once per long rest, and Darkness at 5th.' }],
        },
        {
          value: 'high',
          label: 'High Elf',
          help: 'Prestidigitation, swappable on a long rest.',
          traits: [{ name: 'High Elf Lineage', text: 'You know Prestidigitation and can swap it on a long rest. At 3rd level you can cast Detect Magic once per long rest, and Misty Step at 5th.' }],
        },
        {
          value: 'wood',
          label: 'Wood Elf',
          help: 'Speed 35 and Druidcraft.',
          traits: [{ name: 'Wood Elf Lineage', text: 'Your speed increases to 35 feet. You know Druidcraft. At 3rd level you can cast Longstrider once per long rest, and Pass without Trace at 5th.' }],
        },
      ],
    },
  },
  {
    id: 'halfling',
    name: 'Halfling',
    source: 'srd',
    tagline: 'Small, brave, and famously lucky.',
    blurb:
      'You reroll natural 1s, are hard to frighten, and can slip through the space of anything bigger than you — which is nearly everything.',
    size: 'Small',
    speed: 30,
    darkvision: 0,
    creatureType: 'Humanoid',
    traits: [
      { name: 'Brave', text: 'You have advantage on saving throws to avoid or end the Frightened condition.' },
      { name: 'Halfling Nimbleness', text: 'You can move through the space of any creature larger than you.' },
      { name: 'Luck', text: 'When you roll a 1 on the d20 of a D20 Test, you can reroll and must use the new roll.' },
      { name: 'Naturally Stealthy', text: 'You can hide even when obscured only by a creature at least one size larger than you.' },
    ],
  },
  {
    id: 'gnome',
    name: 'Gnome',
    source: 'srd',
    tagline: 'Small, curious, and stubbornly resistant to magic.',
    blurb:
      'Advantage on the three mental saving throws is one of the best defensive traits in the game. Pick a lineage for illusions or tinkering.',
    size: 'Small',
    speed: 30,
    darkvision: 60,
    creatureType: 'Humanoid',
    saveAdvantage: ['int', 'wis', 'cha'],
    witchlightNote: 'Gnomes have deep Feywild roots — a strong thematic pick here.',
    traits: [
      { name: 'Gnomish Cunning', text: 'You have advantage on Intelligence, Wisdom, and Charisma saving throws.' },
    ],
    choice: {
      id: 'gnomishLineage',
      label: 'Gnomish lineage',
      help: 'Forest gnomes lean illusion; rock gnomes lean gadgets.',
      options: [
        {
          value: 'forest',
          label: 'Forest Gnome',
          help: 'Minor Illusion and Speak with Animals.',
          traits: [{ name: 'Forest Gnome', text: 'You know Minor Illusion. You can cast Speak with Animals a number of times per long rest equal to your Proficiency Bonus.' }],
        },
        {
          value: 'rock',
          label: 'Rock Gnome',
          help: 'Mending, Prestidigitation, and clockwork devices.',
          traits: [{ name: 'Rock Gnome', text: 'You know Mending and Prestidigitation. You can build tiny clockwork devices with Tinker.' }],
        },
      ],
    },
  },
  {
    id: 'dwarf',
    name: 'Dwarf',
    source: 'srd',
    tagline: 'Tough, poison-proof, and hard to kill.',
    blurb:
      'Extra hit points every level, resistance to poison, and tremorsense through stone. The most durable starting species.',
    size: 'Medium',
    speed: 30,
    darkvision: 120,
    creatureType: 'Humanoid',
    hpPerLevel: 1,
    traits: [
      { name: 'Dwarven Resilience', text: 'You have resistance to Poison damage and advantage on saves against being Poisoned.' },
      { name: 'Dwarven Toughness', text: 'Your hit point maximum increases by 1, and by 1 again every time you gain a level.' },
      { name: 'Stonecunning', text: 'As a Bonus Action you gain Tremorsense out to 60 feet on stone surfaces for 10 minutes.' },
    ],
  },
  {
    id: 'tiefling',
    name: 'Tiefling',
    source: 'srd',
    tagline: 'Otherworldly heritage, with spells to match.',
    blurb:
      'Free cantrips that grow into real spells, plus a damage resistance chosen by your legacy. Strong flavour, strong utility.',
    size: 'Small or Medium',
    speed: 30,
    darkvision: 60,
    creatureType: 'Humanoid',
    traits: [
      { name: 'Otherworldly Presence', text: 'You know the Thaumaturgy cantrip.' },
    ],
    choice: {
      id: 'fiendishLegacy',
      label: 'Fiendish legacy',
      help: 'This sets your resistance and your free spells.',
      options: [
        {
          value: 'abyssal',
          label: 'Abyssal',
          help: 'Poison resistance, Poison Spray.',
          traits: [{ name: 'Abyssal Legacy', text: 'Resistance to Poison damage. You know Poison Spray. At 3rd level you can cast Ray of Sickness once per long rest, and Hold Person at 5th.' }],
        },
        {
          value: 'chthonic',
          label: 'Chthonic',
          help: 'Necrotic resistance, Chill Touch.',
          traits: [{ name: 'Chthonic Legacy', text: 'Resistance to Necrotic damage. You know Chill Touch. At 3rd level you can cast False Life once per long rest, and Ray of Enfeeblement at 5th.' }],
        },
        {
          value: 'infernal',
          label: 'Infernal',
          help: 'Fire resistance, Fire Bolt.',
          traits: [{ name: 'Infernal Legacy', text: 'Resistance to Fire damage. You know Fire Bolt. At 3rd level you can cast Hellish Rebuke once per long rest, and Darkness at 5th.' }],
        },
      ],
    },
  },
  {
    id: 'aasimar',
    name: 'Aasimar',
    source: 'srd',
    tagline: 'Touched by the heavens. Healing hands and radiant wings.',
    blurb:
      'You can heal with a touch, resist necrotic and radiant damage, and at 3rd level sprout wings or a halo. Good for anyone who wants to play the light in a dark story.',
    size: 'Small or Medium',
    speed: 30,
    darkvision: 60,
    creatureType: 'Humanoid',
    traits: [
      { name: 'Celestial Resistance', text: 'You have resistance to Necrotic and Radiant damage.' },
      { name: 'Healing Hands', text: 'As a Magic action you can heal a creature you touch for d4s equal to your Proficiency Bonus. Once per long rest.' },
      { name: 'Light Bearer', text: 'You know the Light cantrip.' },
      { name: 'Celestial Revelation', text: 'At 3rd level you can transform to gain wings, a necrotic shroud, or a radiant consumption.' },
    ],
  },
  {
    id: 'goliath',
    name: 'Goliath',
    source: 'srd',
    tagline: 'Giant-blooded. Big, fast, and built to carry things.',
    blurb:
      'Speed 35, count as one size larger for carrying and grappling, and a giant-ancestry perk you can use every rest.',
    size: 'Medium',
    speed: 35,
    darkvision: 0,
    creatureType: 'Humanoid',
    traits: [
      { name: 'Powerful Build', text: 'You count as one size larger for carrying capacity and when pushing, dragging, or lifting.' },
      { name: 'Large Form', text: 'At 5th level you can become Large for 10 minutes once per long rest.' },
    ],
    choice: {
      id: 'giantAncestry',
      label: 'Giant ancestry',
      help: 'Each grants a different once-per-rest perk.',
      options: [
        { value: 'cloud', label: "Cloud's Jaunt", help: 'Teleport 30 feet as a Bonus Action.', traits: [{ name: "Giant Ancestry: Cloud's Jaunt", text: 'As a Bonus Action, teleport up to 30 feet to a space you can see. Uses equal to your Proficiency Bonus per Long Rest.' }] },
        { value: 'fire', label: "Fire's Burn", help: 'Add 1d10 fire damage on a hit.', traits: [{ name: "Giant Ancestry: Fire's Burn", text: 'When you hit with an attack, deal an extra 1d10 Fire damage. Uses equal to your Proficiency Bonus per Long Rest.' }] },
        { value: 'frost', label: "Frost's Chill", help: 'Add 1d6 cold damage and slow the target.', traits: [{ name: "Giant Ancestry: Frost's Chill", text: 'When you hit with an attack, deal an extra 1d6 Cold damage and reduce the target\'s speed by 10 feet. Uses equal to your Proficiency Bonus per Long Rest.' }] },
        { value: 'hill', label: "Hill's Tumble", help: 'Knock a Large or smaller creature Prone.', traits: [{ name: "Giant Ancestry: Hill's Tumble", text: 'When you hit a Large or smaller creature, you can knock it Prone. Uses equal to your Proficiency Bonus per Long Rest.' }] },
        { value: 'stone', label: "Stone's Endurance", help: 'Reduce damage taken by 1d12 + Con modifier.', traits: [{ name: "Giant Ancestry: Stone's Endurance", text: 'As a Reaction, reduce damage you take by 1d12 plus your Constitution modifier. Uses equal to your Proficiency Bonus per Long Rest.' }] },
        { value: 'storm', label: "Storm's Thunder", help: 'Deal 1d8 thunder damage to an attacker as a reaction.', traits: [{ name: "Giant Ancestry: Storm's Thunder", text: 'When you take damage from a creature within 60 feet, use your Reaction to deal 1d8 Thunder damage to it. Uses equal to your Proficiency Bonus per Long Rest.' }] },
      ],
    },
  },
  {
    id: 'dragonborn',
    name: 'Dragonborn',
    source: 'srd',
    tagline: 'Draconic heritage with a breath weapon.',
    blurb:
      'You breathe damage in a cone or line, resist that damage type, and grow wings at 5th level.',
    size: 'Medium',
    speed: 30,
    darkvision: 60,
    creatureType: 'Humanoid',
    traits: [
      { name: 'Breath Weapon', text: 'Replace one attack with a 15-foot cone or 30-foot line dealing 1d10 of your ancestry damage (Dex save for half). Uses equal to Proficiency Bonus per long rest.' },
      { name: 'Damage Resistance', text: 'You have resistance to the damage type of your draconic ancestry.' },
      { name: 'Draconic Flight', text: 'At 5th level you can sprout wings for 10 minutes once per long rest.' },
    ],
    choice: {
      id: 'draconicAncestry',
      label: 'Draconic ancestry',
      help: 'Sets your breath weapon and resistance.',
      options: [
        { value: 'black', label: 'Black — Acid', help: 'Acid damage.', traits: [{ name: 'Draconic Ancestry: Black', text: 'Your Breath Weapon deals Acid damage, and you have resistance to Acid damage.' }] },
        { value: 'blue', label: 'Blue — Lightning', help: 'Lightning damage.', traits: [{ name: 'Draconic Ancestry: Blue', text: 'Your Breath Weapon deals Lightning damage, and you have resistance to Lightning damage.' }] },
        { value: 'brass', label: 'Brass — Fire', help: 'Fire damage.', traits: [{ name: 'Draconic Ancestry: Brass', text: 'Your Breath Weapon deals Fire damage, and you have resistance to Fire damage.' }] },
        { value: 'bronze', label: 'Bronze — Lightning', help: 'Lightning damage.', traits: [{ name: 'Draconic Ancestry: Bronze', text: 'Your Breath Weapon deals Lightning damage, and you have resistance to Lightning damage.' }] },
        { value: 'copper', label: 'Copper — Acid', help: 'Acid damage.', traits: [{ name: 'Draconic Ancestry: Copper', text: 'Your Breath Weapon deals Acid damage, and you have resistance to Acid damage.' }] },
        { value: 'gold', label: 'Gold — Fire', help: 'Fire damage.', traits: [{ name: 'Draconic Ancestry: Gold', text: 'Your Breath Weapon deals Fire damage, and you have resistance to Fire damage.' }] },
        { value: 'green', label: 'Green — Poison', help: 'Poison damage.', traits: [{ name: 'Draconic Ancestry: Green', text: 'Your Breath Weapon deals Poison damage, and you have resistance to Poison damage.' }] },
        { value: 'red', label: 'Red — Fire', help: 'Fire damage.', traits: [{ name: 'Draconic Ancestry: Red', text: 'Your Breath Weapon deals Fire damage, and you have resistance to Fire damage.' }] },
        { value: 'silver', label: 'Silver — Cold', help: 'Cold damage.', traits: [{ name: 'Draconic Ancestry: Silver', text: 'Your Breath Weapon deals Cold damage, and you have resistance to Cold damage.' }] },
        { value: 'white', label: 'White — Cold', help: 'Cold damage.', traits: [{ name: 'Draconic Ancestry: White', text: 'Your Breath Weapon deals Cold damage, and you have resistance to Cold damage.' }] },
      ],
    },
  },
  {
    id: 'orc',
    name: 'Orc',
    source: 'srd',
    tagline: 'Relentless. Gets back up when others do not.',
    blurb:
      'Drop to 0 hit points and stay standing once per rest, plus a burst of speed and temporary hit points every short rest.',
    size: 'Medium',
    speed: 30,
    darkvision: 120,
    creatureType: 'Humanoid',
    traits: [
      { name: 'Adrenaline Rush', text: 'Take the Dash action as a Bonus Action and gain temporary hit points equal to your Proficiency Bonus. Uses equal to Proficiency Bonus, regained on a Short Rest.' },
      { name: 'Relentless Endurance', text: 'When reduced to 0 hit points but not killed outright, you drop to 1 instead. Once per long rest.' },
    ],
  },
]

export const SPECIES_BY_ID = new Map(SPECIES_LIST.map((s) => [s.id, s]))

/** Feywild-flavoured picks, surfaced first in the wizard for this campaign. */
export const WITCHLIGHT_RECOMMENDED = ['fairy', 'harengon', 'elf', 'gnome', 'halfling']
